import { waitedText } from '@/services/claude-fleet/claude-fleet.utils'
import { isOwnedBy } from '@/services/my-work/only-mine.utils'
import { getErrorStatus, isApiError } from '@/utils/error-handler'
import type {
  OversightTab,
  TeamDecision,
  TeamFeedCard,
  TeamReadyPerson,
  TeamReadyPr,
  TeamRecommend,
  TeamTab,
  AttentionTab
} from './claude-team.types'

/** Needs attention: what waits on a person now. */
export const ATTENTION_TABS: AttentionTab[] = ['decisions', 'ready', 'checks']

/** Oversight: what Claude did, for a person to check. */
export const OVERSIGHT_TABS: OversightTab[] = ['approved', 'merged', 'cancelled']

export const TEAM_LISTS: TeamTab[] = [...ATTENTION_TABS, ...OVERSIGHT_TABS]

export const TEAM_LIST_TITLES: Record<TeamTab, string> = {
  decisions: 'Decisions needed',
  ready: 'Ready to merge',
  checks: 'Delivery checks',
  approved: 'Approved by Claude',
  merged: 'Merged by Claude',
  cancelled: 'Cancelled by Claude'
}

export const TEAM_LIST_HELP: Record<TeamTab, string> = {
  decisions:
    'Questions the fleet stopped on, urgent and oldest first. The answer is saved on the ticket, as a pick from the Slack card would be.',
  ready:
    "Every engineer's own PRs that My work counts as ready to merge, by each person's own findings standard, as of their last My work read. Anyone on staff may merge one: your own runner merges it as you, and Bitbucket's merge checks still apply.",
  checks:
    "Where the records disagree: a red or stuck build on a long-lived branch, a release branch develop lacks, a ticket that missed its release's cut, and a ticket whose status does not match its PRs or deploys. Each row links to what fixes it.",
  approved: 'PRs Claude approved that no one has merged yet. Hold withdraws the approval.',
  merged: "Tickets whose PRs Claude's fleet merged in the last 14 days. Report a problem keeps QA from passing them.",
  cancelled: 'Tickets Claude cancelled in the last 14 days. Undo reopens one.'
}

/**
 * The route each list lives on, for a link or the old Team page's redirect. The old page opened on Decisions
 * for a link with no tab (or an unknown one), so those go to Needs attention, the page that needs a person.
 */
export function pageOfList(list: string | undefined): 'claude-needs-attention' | 'claude-oversight' {
  return OVERSIGHT_TABS.includes(list as OversightTab) ? 'claude-oversight' : 'claude-needs-attention'
}

export const DECISION_REASON_TEXT: Record<string, string> = {
  urgent: 'Urgent: High or Highest priority',
  hotfix: 'Hotfix: on an open patch release or labelled hotfix',
  stale: 'Waiting 3 days or more'
}

/** Refusals that leave nothing to do: the card was answered, moved on or cleared, or another click is in progress. */
const SETTLED_REFUSAL =
  /moved on|no open question|question changed|question has changed or been answered|now, not cancelled|being handled|already merged or closed|no card to act on|card is gone/i

export interface TeamActRefusal {
  /** True when nothing is left to do; false when the press failed or may be only partly done. */
  settled: boolean
  text: string
}

/**
 * The fleet answers 409 for every refusal from a card's handler (POST /fleet/team/act), including its own failure,
 * and only its `error` text says which. Unrecognised text counts as a failure, never as answered.
 */
export function teamActRefusal(failure: unknown): TeamActRefusal | null {
  if (getErrorStatus(failure) !== 409) return null
  const text = isApiError(failure) ? failure.response?.data?.error : undefined
  if (typeof text !== 'string' || !text.trim()) return { settled: false, text: 'That card could not be acted on.' }
  return { settled: SETTLED_REFUSAL.test(text), text: text.trim() }
}

export function searchDecisions(decisions: TeamDecision[], search: string): TeamDecision[] {
  const needle = search.trim().toLowerCase()
  if (!needle) return decisions
  return decisions.filter(d =>
    [d.key, d.question, d.summary ?? '', d.assignee ?? ''].some(field => field.toLowerCase().includes(needle))
  )
}

export function searchCards(cards: TeamFeedCard[], search: string): TeamFeedCard[] {
  const needle = search.trim().toLowerCase()
  if (!needle) return cards
  return cards.filter(c => [c.key, c.text, c.summary ?? ''].some(field => field.toLowerCase().includes(needle)))
}

/** People whose ready PRs match the search by ticket, title, repo or the person's name; a person left with none is dropped. */
export function searchReady(people: TeamReadyPerson[], search: string): TeamReadyPerson[] {
  const needle = search.trim().toLowerCase()
  if (!needle) return people
  const matches = (field: string) => field.toLowerCase().includes(needle)
  return people
    .map(person => ({
      ...person,
      prs: matches(person.ownerName) ? person.prs : person.prs.filter(pr => [pr.ticketKey ?? '', pr.title, pr.repo].some(matches))
    }))
    .filter(person => person.prs.length > 0)
}

/** An owner's My work read older than this may no longer match the PR, and the row says so. */
export const READY_STALE_S = 3600

const BUILD_TEXT: Record<string, string> = {
  green: 'Build passed',
  'no-ci': 'No PR build; quiet since the last commit',
  'approval-gate': 'Build waits on an approval step',
  none: 'No build'
}

/** What made one PR ready, as its owner's last My work read saw it, one short phrase each. */
export function readyFacts(pr: TeamReadyPr, now: Date = new Date()): string[] {
  const facts: string[] = []
  if (pr.build) facts.push(BUILD_TEXT[pr.build] ?? `Build ${pr.build}`)
  if (pr.verdict) facts.push(`Claude: ${pr.verdict}`)
  if (typeof pr.approvals === 'number') {
    const need = typeof pr.requiredApprovals === 'number' ? ` (${pr.requiredApprovals} needed)` : ''
    facts.push(`${pr.approvals} ${pr.approvals === 1 ? 'approval' : 'approvals'}${need}`)
  }
  if (pr.destination) facts.push(`into ${pr.destination}`)
  if (pr.readySince && !Number.isNaN(Date.parse(pr.readySince))) {
    facts.push(`ready for ${waitedText(now.getTime() - Date.parse(pr.readySince))}`)
  }
  return facts
}

/** The line under a row saying what Claude recommends and why ("" when the fleet sent no recommendation). */
export function recommendText(recommend: TeamRecommend | undefined): string {
  const action = recommend?.action?.trim()
  if (!action) return ''
  const why = recommend?.why?.trim()
  return why ? `Claude recommends: ${action} — ${why}` : `Claude recommends: ${action}`
}

/** How fresh a person's rows are (their read's age is in the heading), said plainly when old enough to be out of date. */
export function readyFreshness(person: Pick<TeamReadyPerson, 'ageS' | 'ownerName'>): { text: string; stale: boolean } {
  if (person.ageS < READY_STALE_S) return { text: `As of ${person.ownerName}'s last My work read.`, stale: false }
  return {
    text: `From ${person.ownerName}'s last My work read, over an hour ago: these may have changed since. A merge checks each PR again first.`,
    stale: true
  }
}

/** Oldest-ready first, people and their PRs, so the top of the list has waited longest. */
export function sortReady(people: TeamReadyPerson[]): TeamReadyPerson[] {
  const since = (pr: TeamReadyPr) => (pr.readySince ? Date.parse(pr.readySince) : Number.POSITIVE_INFINITY)
  const oldest = (person: TeamReadyPerson) => Math.min(...person.prs.map(since))
  return people
    .map(person => ({ ...person, prs: [...person.prs].sort((a, b) => since(a) - since(b)) }))
    .sort((a, b) => oldest(a) - oldest(b) || a.ownerName.localeCompare(b.ownerName))
}

/** The viewer's own row: the fleet names a person by email, and the test reader sees nobody's. */
export function isViewersReady(person: Pick<TeamReadyPerson, 'owner'>, viewerEmail: string | undefined): boolean {
  return isOwnedBy(person.owner, viewerEmail)
}

/** A Slack user id the fleet sent, never the test reader's hidden-name text. */
export function isSlackUserId(id: string | undefined): id is string {
  return Boolean(id && /^[UW][A-Z0-9]{6,}$/.test(id))
}

/** Opens a DM with that person in the viewer's own Slack. */
export function slackDmUrl(slackUserId: string): string {
  return `https://slack.com/app_redirect?channel=${encodeURIComponent(slackUserId)}`
}

/** The message Nudge the owner copies for the viewer to paste, one line. */
export function nudgeText(pr: Pick<TeamReadyPr, 'repo' | 'id' | 'url' | 'title'>, ownerName: string): string {
  const first = ownerName.split(' ')[0] || ownerName
  return `Hi ${first}, ${pr.repo} #${pr.id} (${pr.title}) looks ready to merge on ADX's Needs attention page. Could you merge it, or say what it is waiting on? ${pr.url}`
}

export function readAgeText(ageS: number): string {
  if (ageS < 60) return 'read just now'
  if (ageS < 3600) return `read ${Math.floor(ageS / 60)} min ago`
  return `read ${Math.floor(ageS / 3600)} h ago`
}

/** Urgent first, then the longest waiting, so the top of the list is what to answer next. */
export function sortDecisions(decisions: TeamDecision[]): TeamDecision[] {
  return [...decisions].sort((a, b) => Number(b.urgent) - Number(a.urgent) || b.ageDays - a.ageDays)
}

export function ageText(days: number): string {
  if (days < 1) return 'today'
  if (days < 2) return '1 day'
  return `${Math.floor(days)} days`
}

/** Three days is when the decisions DM starts counting a decision as waiting too long. */
export const LONG_WAIT_DAYS = 3
